import { useCallback, useEffect, useRef, useState } from 'react';
import { Modal, Select, Typography, message } from 'antd';
import { getOneCCatalog } from '../../api/onec';
import { barcodeScanApi } from '../../api/barcodeScans';
import useBarcodeScanStore from '../../store/barcodeScanStore';
import BarcodeKnowledgeCard from './BarcodeKnowledgeCard';
import { processBarcodeScan } from '../../services/barcodeScanProcessor';

const BarcodeBindModal: React.FC = () => {
  const scan = useBarcodeScanStore(s => s.bindingScan);
  const closeBinding = useBarcodeScanStore(s => s.closeBinding);
  const [options, setOptions] = useState<{ value: string; label: string }[]>([]);
  const [productId, setProductId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const load = useCallback(async (search?: string) => {
    setLoading(true);
    try {
      const rows = await getOneCCatalog({ search, type: 'product' });
      setOptions(rows.map(row => ({
        value: row.id,
        label: `${row.article ? `${row.article} — ` : ''}${row.name}`,
      })));
    } catch (e: any) {
      message.error(e?.response?.data?.detail || 'Не удалось загрузить каталог');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!scan) return;
    setProductId(null);
    void load(scan.knowledge?.suggested_search ?? undefined);
  }, [scan, load]);

  const search = (value: string) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => void load(value), 300);
  };

  const bind = async () => {
    if (!scan || !productId) return;
    setSaving(true);
    try {
      const binding = await barcodeScanApi.bind(scan.raw_code, productId);
      message.success(`Штрихкод привязан к «${binding.product_name}»`);
      closeBinding();
      await processBarcodeScan({ ...scan, outcome: null }, true);
    } catch (e: any) {
      message.error(e?.response?.data?.detail || 'Не удалось сохранить привязку');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title="Привязать штрихкод к товару"
      open={!!scan}
      onCancel={closeBinding}
      onOk={bind}
      okText="Привязать и добавить"
      cancelText="Отмена"
      okButtonProps={{ disabled: !productId, loading: saving }}
      destroyOnClose
    >
      {scan?.knowledge && <BarcodeKnowledgeCard knowledge={scan.knowledge} />}
      <Typography.Paragraph style={{ marginTop: scan?.knowledge ? 16 : 0 }}>
        Код <Typography.Text code>{scan?.raw_code}</Typography.Text> не найден в 1С.
        Выберите товар — привязка сохранится для следующих сканирований.
      </Typography.Paragraph>
      <Select
        showSearch
        value={productId}
        onChange={setProductId}
        onSearch={search}
        onFocus={() => { if (options.length === 0) void load(scan?.knowledge?.suggested_search ?? undefined); }}
        filterOption={false}
        loading={loading}
        options={options}
        placeholder="Найти товар по названию или артикулу"
        style={{ width: '100%' }}
      />
    </Modal>
  );
};

export default BarcodeBindModal;
